<template>
  <canvas class="fill" id="skychart"></canvas>
</template>
<script>
//import axios from "axios";
import * as HA from "@h-a/astrolib";
import Vue from "vue";
import { BootstrapVue, BootstrapVueIcons } from "bootstrap-vue";
import { mapState } from "vuex";

import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";

Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

export default {
  name: "SkyChart",
  props: {
    satMagLimit: {
      type: Number,
      default: 5
    },
    elevLimit: {
      type: Number,
      default: 20
    },
    darkMode: {
      type: Boolean,
      default: false
    },
    showSatLabels: {
      type: Boolean,
      default: false
    },
    showStarLabels: {
      type: Boolean,
      default: true
    },
    showAsterisms: {
      type: Boolean,
      default: true
    },
    showConstellationBoundaries: {
      type: Boolean,
      default: true
    },
    showConstellationLabels: {
      type: Boolean,
      default: true
    },
    showEcliptic: {
      type: Boolean,
      default: true
    },
    showEquator: {
      type: Boolean,
      default: true
    },
    showSkyShading: {
      type: Boolean,
      default: true
    },
    comets: {
      type: Array,
      default: null
    }
  },

  computed: mapState([
    "utc",
    "location",
    "culture",
    "strings",
    "staticData",
    "satellites",
    "selectedSat",
    "currentlyVisibleSatellites"
    
  ]),

  watch: {
    utc: function() {
      this.render();
    },
    location: function() {
      this.render();
    },
    darkMode: function() {
      this.render();
    },
    showConstellationBoundaries: function() {
      this.render();
    },
    showSkyShading: function() {
      this.render();
    },
    showConstellationLabels: function() {
      this.render();
    },
    showAsterisms: function() {
      this.render();
    },
    showEquator: function() {
      this.render();
    },
    showEcliptic: function() {
      this.render();
    },
    showStarLabels: function() {
      this.render();
    },
    showSatLabels: function() {
      this.render();
    },
    satellites: function() {
      this.render();
    }
  },
  data() {
    return {
      from1875: null,
      zenithJ2000: null,
      observerJ2000: null,
      rotMatrix: null,
      rotMatrixInv: null,
      inversePrecessionMatrix: null,
      radsPerPixel: null,
      ctx: null,
      canvas: null,
      isFullscreen: false,
      //dynamicBlocks: {},
      sunCheby: null,
      moonCheby: null,
      selectableObjects: null,
      selectedObject: null,
      nearestObject: null,
      gha: 0,
      currentDynamicBlock: null
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    init: function() {
      this.canvas = document.getElementById("skychart");
      this.ctx = this.canvas.getContext("2d");

      // event listeners
      let self = this;
      this.canvas.addEventListener(
        "mousemove",
        function(evt) {
          self.onMouseMove(evt);
        },
        false
      );
      this.canvas.addEventListener(
        "mousedown",
        function(evt) {
          self.onMouseDown(evt);
        },
        false
      );

      /* The rotation matrix to transform vectors from the 1875 reference frame
         in which the constellation boundaries are defined to J2000 */
      this.from1875 = HA.Earth.getPrecessionMatrix(
        new Date(1875, 0, 1).getTime()
      ).transpose();
    },

    render: function() {
      if (!this.strings || !this.location || !this.staticData) return;

      this.currentDynamicBlock = this.$store.getters.getDynamicDataBlockForTime(
        this.utc
      );

      let ctx = this.ctx;
      this.selectableObjects = [];

      // fill background of chart rectangle
      let w = this.canvas.clientWidth;
      let h = this.canvas.clientHeight;
      this.canvas.width = w;
      this.canvas.height = h;
      ctx.fillStyle = this.darkMode ? "black" : "white";
      //ctx.fillRect(0, 0, w, h);

      // center the chart at the top of the parent and make it as large as possible such that it is entirely visible
      if (w > h) {
        this.x0 = w / 2;
        this.y0 = h / 2;
      } else {
        this.x0 = w / 2;
        this.y0 = w / 2;
      }
      let azimuthRingWidth = 20;
      let outerRadius = Math.min(w, h) / 2 - 1;
      let radius = outerRadius - azimuthRingWidth;
      this.radsPerPixel = HA.HALFPI / radius;

      this.gha = HA.Earth.gha(this.utc);

      // get the zenith in equatorial coordinates of date
      let zenithOfDate = this.location.zenith.rotateAboutZAxis(this.gha);

      // matrix to account for precession
      this.rotMatrix = HA.Earth.getPrecessionMatrix(this.utc);
      this.inversePrecessionMatrix = this.rotMatrix.transpose();

      // convert zenith to J2000 coordinates
      this.zenithJ2000 = this.inversePrecessionMatrix.multiplyVector(
        zenithOfDate
      );

      // observer position relative to Earth center in J2000 coordinates
      this.observerJ2000 = this.inversePrecessionMatrix.multiplyVector(
        this.location.position.rotateAboutZAxis(this.gha)
      );

      // compute matrix to transform from J2000 to topocentric coordinates
      this.rotMatrix = this.location
        .getGeocentricToTopographicMatrix(this.gha)
        .multiplyMatrix(this.rotMatrix);

      this.rotMatrixInv = this.rotMatrix.transpose();

      // azimuth ring
      this.renderAzimuthRing(ctx, radius, outerRadius);

      // sky background
      this.renderSkyBackground(ctx, radius);

      // set clip region to visible part of sky
      ctx.beginPath();
      ctx.arc(this.x0, this.y0, radius, 0, 2 * Math.PI, true);
      ctx.clip();

      this.renderConstellationBoundaries(ctx, radius);
      this.renderEquator(ctx);
      this.renderEclipticPlane(ctx);
      this.renderAsterisms(ctx);
      this.renderStars(ctx);
      this.renderConstellationLabels(ctx);
      this.renderStarLabels(ctx);
      this.renderPlanets(ctx);
      this.renderSun(ctx);
      this.renderComets(ctx);
      this.renderMoon(ctx);
      this.renderSatellites(ctx);
      // this.renderSelectedObject(ctx);
    },

    drawBoundarySeg: function(args) {
      // compute visibility of start and end points
      let visTest = -0.2;
      let skyS = this.skyFrom1875(args.ra1, args.dec1);
      let skyE = this.skyFrom1875(args.ra2, args.dec2);

      // only draw the boundary segment either start or end points pass the visibility test
      if (
        skyS.dotProduct(this.zenithJ2000) < visTest &&
        skyE.dotProduct(this.zenithJ2000) < visTest
      )
        return;

      let pnt = this.j2000ToCanvas(skyS);
      this.ctx.moveTo(pnt.x, pnt.y);

      // we have to split up segments of the boundary, because they can be very long and if we simply connect the end points, they will not match the actual boundary
      // is this a great circle of Right Ascension?
      if (args.ra1 == args.ra2) {
        let dDecl = args.dec2 > args.dec1 ? 7200 : -7200;
        let declas = args.dec1 + dDecl;
        while (declas < args.dec1 != declas < args.dec2) {
          pnt = this.j2000ToCanvas(this.skyFrom1875(args.ra1, declas));
          this.ctx.lineTo(pnt.x, pnt.y);
          declas += dDecl;
        }
      } else {
        // this is a parallel of declination
        if (Math.abs(args.ra1 - args.ra2) > 12 * 3600) {
          //The line must cross  RA=0
          if (args.ra1 < 12 * 3600) args.ra1 += 24 * 3600;
          else args.ra2 += 24 * 3600;
        }
        let dRA = args.ra2 > args.ra1 ? 480 : -480;
        let ra = args.ra1 + dRA;
        while (ra < args.ra1 != ra < args.ra2) {
          pnt = this.j2000ToCanvas(this.skyFrom1875(ra, args.dec1));
          this.ctx.lineTo(pnt.x, pnt.y);
          ra += dRA;
        }
      }

      pnt = this.j2000ToCanvas(skyE);
      this.ctx.lineTo(pnt.x, pnt.y);
    },

    skyFrom1875: function(ra, dec) {
      return this.from1875.multiplyVector(
        HA.Vector.fromAzElev(
          (ra / 240) * HA.DEGTORAD,
          (dec / 3600) * HA.DEGTORAD
        )
      );
    },

    /** convert a unit vector representing a point in J2000 coordinates to a 2D point on the canvas */
    j2000ToCanvas: function(vecSky) {
      // convert to topocentric coords
      let topoVec = this.rotMatrix.multiplyVector(vecSky);
      return this.topoToCanvas(topoVec);
    },

    /** convert a  vector representing a point in topocentric coordinates to a 2D point on the canvas */
    topoToCanvas: function(topoVec) {
      let angleFromFOVCentre = HA.HALFPI - topoVec.latitude;
      let angleFromScreenX = Math.PI - topoVec.longitude;
      let x =
        this.x0 +
        (angleFromFOVCentre * Math.cos(angleFromScreenX)) / this.radsPerPixel;
      let y =
        this.y0 -
        (angleFromFOVCentre * Math.sin(angleFromScreenX)) / this.radsPerPixel;
      return { x: x, y: y };
    },

    renderConstellationBoundaries: function(ctx) {
      if (this.showConstellationBoundaries) {
        ctx.strokeStyle = "#808000";

        let cons = this.staticData.cons;
        for (let abbr in cons) {
          if (Object.prototype.hasOwnProperty.call(cons, abbr)) {
            // each constellation has only one continuous boundary, except Serpens, which has two
            let bounds = cons[abbr].bounds;
            ctx.beginPath();
            for (let b = 0; b < bounds.length; b++) {
              let bound = bounds[b];

              for (let bp = 0; bp < bound.length - 3; bp += 2) {
                this.drawBoundarySeg({
                  ra1: bound[bp],
                  dec1: bound[bp + 1],
                  ra2: bound[bp + 2],
                  dec2: bound[bp + 3]
                });
              }

              // close path to first point
              this.drawBoundarySeg({
                ra1: bound[bound.length - 2],
                dec1: bound[bound.length - 1],
                ra2: bound[0],
                dec2: bound[1]
              });

              ctx.stroke();
            }
          }
        }
      }
    },

    renderAzimuthRing: function(ctx, radius, outerRadius) {
      // background for azimuth scale
      //ctx.fillStyle = "#444444";
      ctx.fillStyle = "#303030";
      ctx.strokeStyle = "#999999";
      ctx.beginPath();
      ctx.arc(this.x0, this.y0, outerRadius, 0, HA.TWOPI);
      ctx.fill();
      ctx.stroke();

      // azimuth angles every 10 degrees
      ctx.font = "11px Arial";
      ctx.fillStyle = "#999999";
      ctx.strokeStyle = "#999999";
      ctx.textBaseline = "bottom";
      ctx.textAlign = "center";
      let textRad = radius + 4;
      for (let az = 10; az < 360; az += 10) {
        if (az % 90 === 0) continue;

        ctx.save();
        let azrad = (az * Math.PI) / 180;
        let bottom = az > 90 && az < 270;
        let x = this.x0 - textRad * Math.sin(azrad);
        let y = this.y0 - textRad * Math.cos(azrad);
        ctx.translate(x, y);
        ctx.rotate(bottom ? Math.PI - azrad : -azrad);
        ctx.translate(-x, -y);
        ctx.textBaseline = bottom ? "top" : "bottom";
        ctx.fillText(az.toFixed() + "°", x, y);
        ctx.restore();

        // tick mark
        ctx.beginPath();
        x = this.x0 - radius * Math.sin(azrad);
        y = this.y0 - radius * Math.cos(azrad);
        ctx.moveTo(x, y);
        x = this.x0 - textRad * Math.sin(azrad);
        y = this.y0 - textRad * Math.cos(azrad);
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      // compass direction labels
      ctx.font = "18px Arial";
      ctx.textAlign = "center";

      ctx.textBaseline = "bottom";
      ctx.fillText(this.strings["compass.N"], this.x0, this.y0 - radius);

      ctx.textBaseline = "top";
      ctx.fillText(this.strings["compass.S"], this.x0, this.y0 + radius);

      ctx.save();
      ctx.translate(this.x0 - radius, this.y0);
      ctx.rotate(-Math.PI / 2);
      ctx.translate(radius - this.x0, -this.y0);
      ctx.textBaseline = "bottom";
      ctx.fillText(this.strings["compass.E"], this.x0 - radius, this.y0);
      ctx.restore();

      ctx.save();
      ctx.translate(this.x0 + radius, this.y0);
      ctx.rotate(Math.PI / 2);
      ctx.translate(-this.x0 - radius, -this.y0);
      ctx.textBaseline = "bottom";
      ctx.fillText(this.strings["compass.W"], this.x0 + radius, this.y0);
      ctx.restore();
    },

    renderSkyBackground: function(ctx, radius) {
      if (this.showSkyShading) {
        let grd = ctx.createRadialGradient(
          this.x0,
          this.y0,
          radius * 0.8,
          this.x0,
          this.y0,
          radius
        );
        grd.addColorStop(0, "black");
        grd.addColorStop(1, "#0000c0");
        ctx.fillStyle = grd;
      } else ctx.fillStyle = "black";
      ctx.beginPath();
      ctx.arc(this.x0, this.y0, radius, 0, HA.TWOPI);
      ctx.stroke();
      ctx.fill();
    },

    renderEclipticPlane: function(ctx) {
      if (this.showEcliptic) {
        ctx.strokeStyle = "rgb(128,0,128)";
        ctx.beginPath();
        for (let iDeg = 0; iDeg < 361; ++iDeg) {
          let eclipticPnt = new HA.Vector.fromAzElev(iDeg * HA.DEGTORAD, 0);

          // convert to J2000 coords.
          eclipticPnt = eclipticPnt.rotateAboutXAxis(HA.OBLIQUITY_2000);

          let pnt = this.j2000ToCanvas(eclipticPnt);

          if (eclipticPnt.dotProduct(this.zenithJ2000) > -0.2 && iDeg > 0)
            ctx.lineTo(pnt.x, pnt.y);
          else ctx.moveTo(pnt.x, pnt.y);
        }
        ctx.stroke();
      }
    },

    renderEquator: function(ctx) {
      if (this.showEquator) {
        ctx.strokeStyle = "rgb(128,128,128)";
        ctx.beginPath();
        for (let iDeg = 0; iDeg < 361; ++iDeg) {
          let equatorPnt = new HA.Vector.fromAzElev(iDeg * HA.DEGTORAD, 0);

          let pnt = this.j2000ToCanvas(equatorPnt);

          if (equatorPnt.dotProduct(this.zenithJ2000) > -0.2 && iDeg > 0)
            ctx.lineTo(pnt.x, pnt.y);
          else ctx.moveTo(pnt.x, pnt.y);
        }
        ctx.stroke();
      }
    },

    renderAsterisms: function(ctx) {
      if (this.showAsterisms) {
        ctx.strokeStyle = "rgb(160,0,0)";
        ctx.beginPath();
        let lines = this.staticData.lines;
        let stars = this.staticData.stars;
        for (let constell = 0; constell < lines.length; constell++) {
          for (let i = 0; i < lines[constell].length; i++) {
            let ra = stars["s" + lines[constell][i]][0];
            let decl = stars["s" + lines[constell][i]][1];
            let skyPos = new HA.Vector.fromAzElev(ra, decl);
            let pnt = this.j2000ToCanvas(skyPos);
            if (i == 0 || skyPos.dotProduct(this.zenithJ2000) < -0.5)
              ctx.moveTo(pnt.x, pnt.y);
            else ctx.lineTo(pnt.x, pnt.y);
          }
        }
        ctx.stroke();
      }
    },

    renderStars: function(ctx) {
      let stars = this.staticData.stars;
      ctx.strokeStyle = "white";
      ctx.fillStyle = "white";
      for (let starid in stars) {
        let ra = stars[starid][0];
        let decl = stars[starid][1];
        let skyPos = new HA.Vector.fromAzElev(ra, decl);
        if (skyPos.dotProduct(this.zenithJ2000) > 0) {
          let pnt = this.j2000ToCanvas(skyPos);
          this.drawStarlikeObject(ctx, pnt, stars[starid][2]);
          //this.selectableObjects[starid] = pnt;
        }
      }
    },

    drawStarlikeObject(ctx, pnt, mag) {
      let deltaMag = 5 - mag;
      let radius = deltaMag * deltaMag * (this.radius / 4000);

      if (deltaMag < 0.5) radius = 0.5;
      else {
        // make area proportional to luminosity
        //let lumi = Math.pow(10, deltaMag / 2.5);
        radius = deltaMag;
      }
      ctx.beginPath();
      ctx.arc(pnt.x, pnt.y, radius, 0, HA.TWOPI);
      ctx.fill();
    },

    renderConstellationLabels: function(ctx) {
      if (this.showConstellationLabels && this.strings) {
        ctx.fillStyle = "#808000";
        ctx.font = "11px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        let cons = this.staticData.cons;
        for (let abbr in cons) {
          if (Object.prototype.hasOwnProperty.call(cons, abbr)) {
            let con = cons[abbr];
            let ra = con.ra;
            let decl = con.dec;
            let skyPos = new HA.Vector.fromAzElev(ra, decl);
            let pnt = this.j2000ToCanvas(skyPos);
            // only show constellations which are visible above the horizon
            if (skyPos.dotProduct(this.zenithJ2000) > 0)
              ctx.fillText(this.strings["con." + abbr], pnt.x, pnt.y);
          }
        }
      }
    },

    renderStarLabels: function(ctx) {
      if (this.showStarLabels) {
        ctx.fillStyle = "#808080";
        ctx.font = "11px Arial";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        for (let ix = 0; ix < this.staticData.starNames.length; ix++) {
          let starid = this.staticData.starNames[ix];
          let star = this.staticData.stars[starid];
          let starName = star[7];
          let ra = star[0];
          let decl = star[1];
          let skyPos = new HA.Vector.fromAzElev(ra, decl);
          let pnt = this.j2000ToCanvas(skyPos);
          // only show stars which are visible above the horizon
          if (skyPos.dotProduct(this.zenithJ2000) > 0)
            ctx.fillText(starName, pnt.x + 7, pnt.y);
        }
      }
    },

    renderComets: function(ctx) {
      if (!this.comets)
        return;

      for (let i = 0; i < this.comets.length; ++i) {
        let comet = this.comets[i];
        let j2000Pos = comet.astrometricPosition;
        let cometPos = new HA.Vector(j2000Pos.x, j2000Pos.y, j2000Pos.z).normalize();
        if (cometPos.dotProduct(this.zenithJ2000) > 0) {
          let pnt = this.j2000ToCanvas(cometPos);
          let sunPnt = this.j2000ToCanvas(this.sunPos);
          let sunDirection = Math.atan2(sunPnt.y - pnt.y, sunPnt.x - pnt.x);
          this.drawComet(ctx, comet, pnt, sunDirection, 3, 18);
        }
        //this.selectableObjects["Sun"] = pnt;
      }
    },

    drawComet: function(ctx, comet, center, sunDirection, comaRadius, tailLength) {
          ctx.strokeStyle = "cyan";
          ctx.fillStyle = "cyan";
          //ctx.stroke();
          //ctx.fill();
          ctx.beginPath();
          let angle = sunDirection + Math.PI;
          ctx.moveTo(center.x, center.y);
          ctx.lineTo(center.x + tailLength * Math.cos(angle), center.y + tailLength * Math.sin(angle));
          angle += 0.3;
          tailLength *= 0.9;
          ctx.moveTo(center.x, center.y);
          ctx.lineTo(center.x + tailLength * Math.cos(angle), center.y + tailLength * Math.sin(angle));
          angle -= 0.6;
          ctx.moveTo(center.x, center.y);
          ctx.lineTo(center.x + tailLength * Math.cos(angle), center.y + tailLength * Math.sin(angle));
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(center.x, center.y, comaRadius, 0, HA.TWOPI);
          ctx.stroke();
          ctx.fill();
          ctx.font = "11px Arial";
          //ctx.fillStyle = "white";
          ctx.textAlign = "end";
          ctx.fillText(comet.id + ' ' + comet.name, center.x - 8, center.y);
  
    },

    renderSun: function(ctx) {
      if (!this.currentDynamicBlock || !this.currentDynamicBlock.sunCheby)
        return;

      this.sunPos = this.currentDynamicBlock.sunCheby.getPos(this.utc);
      if (this.sunPos.dotProduct(this.zenithJ2000) > 0) {
        let pnt = this.j2000ToCanvas(this.sunPos);
        let innerRadius = 6;
        this.drawStar(ctx, pnt.x, pnt.y, innerRadius, 12, 8);
        ctx.strokeStyle = "red";
        ctx.fillStyle = "orange";
        ctx.stroke();
        ctx.fill();
        ctx.beginPath();
        ctx.arc(pnt.x, pnt.y, innerRadius, 0, HA.TWOPI);
        ctx.fillStyle = "yellow";
        ctx.stroke();
        ctx.fill();
        //this.selectableObjects["Sun"] = pnt;
      }
    },

    renderMoon: function(ctx) {
      if (!this.currentDynamicBlock || !this.currentDynamicBlock.moonCheby)
        return;

      // for the Moon, it's important to allow for parallax
      this.moonPos = this.currentDynamicBlock.moonCheby
        .getPos(this.utc)
        .subtract(this.observerJ2000);
      if (this.moonPos.dotProduct(this.zenithJ2000) > 0) {
        let pnt = this.j2000ToCanvas(this.moonPos);
        let innerRadius = 6;
        ctx.beginPath();
        ctx.arc(pnt.x, pnt.y, innerRadius, 0, HA.TWOPI);
        ctx.fillStyle = "gray";
        ctx.fill();
        ctx.font = "11px Arial";
        ctx.fillStyle = "white";
        ctx.fillText(this.strings["moon"], pnt.x + 8, pnt.y);
        //this.selectableObjects["moon"] = pnt;
      }
    },

    renderPlanets: function(ctx) {
      if (!this.currentDynamicBlock || !this.currentDynamicBlock.moonCheby)
        return;

      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.font = "11px Arial";

      for (let p = 0; p < this.currentDynamicBlock.planets.length; p++) {
        let skypos = this.currentDynamicBlock.planets[p].cheby.getPos(
          this.utc
        );
        if (skypos.dotProduct(this.zenithJ2000) > 0) {
          let pnt = this.j2000ToCanvas(skypos);
          ctx.beginPath();
          ctx.arc(pnt.x, pnt.y, 4, 0, HA.TWOPI);
          ctx.fillStyle = "white";
          ctx.fill();
          ctx.fillStyle = "orange";
          ctx.fillText(
            this.strings[
              "planet." + this.currentDynamicBlock.planets[p].name
            ],
            pnt.x + 6,
            pnt.y
          );
          //this.selectableObjects["moon"] = pnt;
        }
      }
    },

    renderSatellites: function(ctx) {
      let visibleSats = [];

      if (!this.satellites || !this.sunPos) return;

      let selectedSatVisible = false;

      for (let s = 0; s < this.satellites.length; s++) {
        let sat = this.satellites[s];
        if (sat.vis) {
          ctx.fillStyle = "lime";
          let satData = this.getSatelliteData(this.utc, sat);

          if (
            satData &&
            satData.topoPos.latitude > this.elevLimit * HA.DEGTORAD &&
            satData.mag <= this.satMagLimit &&
            satData.isSunlit
          ) {
            this.drawStarlikeObject(ctx, satData.pnt, satData.mag);
            visibleSats.push(satData);
            this.selectableObjects.push({
              type: "sat",
              obj: satData,
              pnt: satData.pnt
            });

            if (this.showSatLabels) {
              this.renderNearestObject(ctx, satData.pnt, sat.name);
            }

            if (
              this.selectedSat &&
              satData.sat.id === this.selectedSat.sat.id
            ) {
              this.$store.commit("setSelectedSat", satData);
              selectedSatVisible = true;
            }

            if (
              this.nearestObject &&
              this.nearestObject.obj.sat.id === satData.sat.id
            )
              this.renderNearestObject(ctx, satData.pnt, sat.name);
          }
        }
      }

      if (selectedSatVisible) {
        this.renderSelectedSat(ctx);
        
      } else {
        this.$store.commit("setSelectedSat", null);
      }
      this.$store.commit("setCurrentlyVisibleSatellites", visibleSats);
    },

    drawStar: function(ctx, x, y, innerRadius, outerRadius, numPoints) {
      ctx.beginPath();

      let da = HA.TWOPI / numPoints;
      ctx.moveTo(x, y - outerRadius);
      for (let i = 0; i < numPoints; i++) {
        let angle = (i + 0.5) * da;
        ctx.lineTo(
          x + innerRadius * Math.sin(angle),
          y - innerRadius * Math.cos(angle)
        );
        angle = (i + 1) * da;
        ctx.lineTo(
          x + outerRadius * Math.sin(angle),
          y - outerRadius * Math.cos(angle)
        );
      }
      ctx.lineTo(x, y - outerRadius);
    },

    onMouseDown: function(evt) {
      let mousePos = this.getMousePos(evt);

      // see if we are in the selection region of one or more selectable objects
      let nearestObject = null;
      let minDist = 50;
      for (let ix = 0; ix < this.selectableObjects.length; ix++) {
        let obj = this.selectableObjects[ix];
        let dx = mousePos.x - obj.pnt.x;
        let dy = mousePos.y - obj.pnt.y;
        let dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          nearestObject = obj;
          minDist = dist;
        }
      }

      if (nearestObject) {
        // change the selected object to the nearest object
        this.selectedObject = nearestObject;

        if (this.selectedObject.type === "sat") {
          this.$store.commit("setSelectedSat", this.selectedObject.obj);
        }
      } else {
        // clear selected object
        this.selectedObject = null;
        this.$store.commit("setSelectedSat", null);
      }
      this.render();
    },

    onMouseMove: function(evt) {
      let mousePos = this.getMousePos(evt);

      // see if we are in the selection region of one or more selectable objects
      let nearestObject = null;
      let minDist = 50;
      for (let ix = 0; ix < this.selectableObjects.length; ix++) {
        let obj = this.selectableObjects[ix];
        let dx = mousePos.x - obj.pnt.x;
        let dy = mousePos.y - obj.pnt.y;
        let dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          nearestObject = obj;
          minDist = dist;
        }
      }

      if (nearestObject !== this.nearestObject) {
        // redraw with the name of the nearest object shown
        this.nearestObject = nearestObject;
        this.render();
      }
    },

    renderSelectedSat: function(ctx) {
      // highlight selected satellite
      if (!this.selectedSat) return;

      this.renderSelectedSatOrbit(ctx);

      let satData = this.selectedSat;
      ctx.strokeStyle = "#00ff00";
      ctx.beginPath();
      ctx.arc(satData.pnt.x, satData.pnt.y, 8, 0, HA.TWOPI);
      ctx.stroke();

      // direction of flight
      let satData2 = this.getSatelliteData(
        this.utc + 5000,
        this.selectedSat.sat
      );
      let direction = Math.atan2(
        satData2.pnt.y - satData.pnt.y,
        satData2.pnt.x - satData.pnt.x
      );
      let arrowPnt = {
        x: satData.pnt.x + 30 * Math.cos(direction),
        y: satData.pnt.y + 30 * Math.sin(direction)
      };
      this.drawArrow(ctx, arrowPnt, 15, direction, 40, 120);
    },

    getSatelliteData: function(t, sat) {
      // the SGP4 model gives the position of the satellite in true-equator-mean-equinox of epoch coordinates (TEME)
      // this can throw an exception if the propagation algorithm determines the satellite should have reentered
      let temePos;
      try {
        temePos = sat.orbit.getPos(t);
      } catch {
        return null;
      }

      // get current observer in geocentric inertial coordinates position by rotating through the Greenwich hour angle
      let gha = HA.Earth.gha(t);
      let observerPos = this.location.position.rotateAboutZAxis(gha);

      // get the satellite position relative to user
      let satRelPos = temePos.subtract(observerPos);
      // convert relative satellite position to  topocentric coordinates
      let topoPos = this.location
        .getGeocentricToTopographicMatrix(gha)
        .multiplyVector(satRelPos);

      let pnt = this.topoToCanvas(topoPos);
      let mag = this.location.getSatelliteMag(sat.mag, satRelPos, this.sunPos);

      return {
        sat: sat,
        pnt: pnt,
        mag: mag,
        topoPos: topoPos,
        isSunlit: HA.isSatelliteSunlit(temePos, this.sunPos)
      };
    },

    renderSelectedSatOrbit: function(ctx) {
      if (!this.selectedSat) return;
      ctx.strokeStyle = "#006000";
      ctx.beginPath();

      // go backwards from current position
      let deltat = 5000;
      let satData = this.getSatelliteData(this.utc, this.selectedSat.sat);
      let startPnt = satData.pnt;
      ctx.moveTo(startPnt.x, startPnt.y);

      for (let t = this.utc - deltat; t > this.utc - 600000; t -= deltat) {
        let satData = this.getSatelliteData(t, this.selectedSat.sat);

        // is satellite sunlit?
        if (satData.isSunlit) {
          ctx.lineTo(satData.pnt.x, satData.pnt.y);
        } else {
          // find the accurate transition time
          t = this.getShadowChangeTime(t, t + deltat, this.selectedSat.sat);
          satData = this.getSatelliteData(t, this.selectedSat.sat);
          ctx.lineTo(satData.pnt.x, satData.pnt.y);
          break;
        }
      }
      ctx.stroke();

      // go forwards from current position
      ctx.moveTo(startPnt.x, startPnt.y);
      for (let t = this.utc + deltat; t < this.utc + 600000; t += deltat) {
        let satData = this.getSatelliteData(t, this.selectedSat.sat);

        // is satellite sunlit?
        if (satData.isSunlit) ctx.lineTo(satData.pnt.x, satData.pnt.y);
        else {
          // find the accurate transition time
          t = this.getShadowChangeTime(t - deltat, t, this.selectedSat.sat);
          satData = this.getSatelliteData(t, this.selectedSat.sat);
          ctx.lineTo(satData.pnt.x, satData.pnt.y);
          break;
        }
      }
      ctx.stroke();
    },

    getShadowChangeTime(t1, t2, sat) {
      let dt = (t2 - t1) / 2;
      let t = t1 + dt;
      let sunlit1 = this.getSatelliteData(t1, sat).isSunlit;
      //let sunlit2 = this.getSatelliteData(t2, sat).isSunlit;
      while (dt > 50) {
        let sunlit = this.getSatelliteData(t, sat).isSunlit;
        if (sunlit == sunlit1) {
          t1 = t;
        } else {
          t2 = t;
        }
        dt = dt / 2;
        t = t1 + dt;
      }
      return t;
    },

    // draw an arrow head with the point at pnt and pointing in direction angle (anti-clockwise from x-axis)
    drawArrow(
      ctx,
      pnt,
      length,
      direction,
      smallAngleDegrees,
      largeAngleDegrees
    ) {
      ctx.beginPath();
      ctx.moveTo(pnt.x, pnt.y);
      let radLarge = (largeAngleDegrees * HA.DEGTORAD) / 2;
      let radSmall = (smallAngleDegrees * HA.DEGTORAD) / 2;
      let x = pnt.x - length * Math.cos(direction - radSmall);
      let y = pnt.y - length * Math.sin(direction - radSmall);
      ctx.lineTo(x, y);

      //Compute length of central line of arrow head
      let d =
        length * (Math.cos(radSmall) - Math.sin(radSmall) / Math.tan(radLarge));
      x = pnt.x - d * Math.cos(direction);
      y = pnt.y - d * Math.sin(direction);
      ctx.lineTo(x, y);
      x = pnt.x - length * Math.cos(direction + radSmall);
      y = pnt.y - length * Math.sin(direction + radSmall);
      ctx.lineTo(x, y);
      ctx.lineTo(pnt.x, pnt.y);
      ctx.fill();
    },

    renderNearestObject: function(ctx, pnt, label) {
      // highlight nearest object and show label
      if (!pnt) return;
      ctx.strokeStyle = "#ffff00";
      ctx.beginPath();
      //ctx.arc(pnt.x, pnt.y, 8, 0, HA.TWOPI);
      ctx.moveTo(pnt.x + 4, pnt.y);
      ctx.lineTo(pnt.x + 12, pnt.y);
      ctx.stroke();

      ctx.fillStyle = "#ffff00";
      ctx.font = "11px Arial";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(label, pnt.x + 14, pnt.y);
    },

    getMousePos: function(evt) {
      let rect = this.canvas.getBoundingClientRect();
      return {
        x:
          ((evt.clientX - rect.left) / (rect.right - rect.left)) *
          this.canvas.width,
        y:
          ((evt.clientY - rect.top) / (rect.bottom - rect.top)) *
          this.canvas.height
      };
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.fullscreen {
  position: absolute;
  top: 5px;
  right: 60px;
}
.fill {
  width: 100%;
  height: 100%;
}
</style>
